import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2 } from 'lucide-react';
import { Eye, EyeSlash, ArrowLeft, MapPin, Envelope } from '@phosphor-icons/react';
import { TERRITOIRES } from '../constants/countries';

export function RegisterPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [territoire, setTerritoire] = useState('');
  const [raisonSociale, setRaisonSociale] = useState('');
  const [siret, setSiret] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    const siretClean = siret.replace(/\s/g, '');
    if (siretClean && !/^\d{14}$/.test(siretClean)) {
      setError('Le SIRET doit contenir exactement 14 chiffres');
      return;
    }

    setLoading(true);

    const fullName = `${prenom.trim()} ${nom.trim()}`.trim();
    // Rôle 'prospect' par défaut — le super admin l'assignera après validation.
    const result = await register(email, password, fullName, 'prospect', territoire || null, {
      raison_sociale: raisonSociale.trim() || null,
      siret: siretClean || null,
    });
    
    if (result.success) {
      setSubmitted(true);
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ 
          backgroundImage: `url('https://images.unsplash.com/photo-1546370316-59ca2ba17db5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxtYXJ0aW5pcXVlJTIwbGFuZHNjYXBlJTIwb2NlYW4lMjBzdW5zZXR8ZW58MHx8fHwxNzc1MjU4NjUwfDA&ixlib=rb-4.1.0&q=85')` 
        }}
      >
        <div className="absolute inset-0 bg-[#2D5A4C]/40"></div>
        <div className="relative z-10 flex flex-col justify-end p-12">
          <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope' }}>
            Rejoignez l'écosystème
          </h1>
          <p className="text-white/90 text-lg max-w-md">
            Guadeloupe, Martinique, Guyane, Réunion, Mayotte - Unis pour l'ESS.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#F9F8F5]">
        <Card className="w-full max-w-md border-[#E8E6E1] shadow-sm">
          <CardHeader className="text-center relative">
            <Link
              to="/"
              className="absolute left-4 top-4 inline-flex items-center gap-1.5 text-xs text-[#5E6D66] hover:text-[#2D5A4C] font-semibold transition-colors"
              data-testid="register-back-home"
            >
              <ArrowLeft size={14} weight="bold" /> Accueil
            </Link>
            <div className="w-12 h-12 bg-[#2D5A4C] rounded-xl flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">OS</span>
            </div>
            <CardTitle className="text-2xl font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>
              Créer un compte
            </CardTitle>
            <CardDescription className="text-[#5E6D66]">
              Rejoignez Objectif SCOP Outremer
            </CardDescription>
            <p className="mt-2 text-[11px] text-[#5E6D66] italic px-2" data-testid="register-note">
              Après création, votre fiche contact sera examinée par un super administrateur qui affinera votre rôle et vos accès.
            </p>
          </CardHeader>
          <CardContent>
            {submitted ? (
              <div className="text-center py-6" data-testid="register-success">
                <div className="w-14 h-14 bg-[#80C51F]/15 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Envelope size={28} weight="fill" className="text-[#2D5A4C]" />
                </div>
                <h3 className="text-lg font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>Vérifiez votre boîte mail</h3>
                <p className="mt-2 text-sm text-[#5E6D66] leading-relaxed">
                  Un email de confirmation vient d'être envoyé à <strong className="text-[#1A2421]">{email}</strong>.
                  Cliquez sur le lien qu'il contient pour activer votre espace, puis connectez-vous pour compléter votre profil.
                </p>
                <p className="mt-3 text-xs text-[#8A968F]">Le lien est valable 24 heures. Pensez à vérifier vos spams.</p>
                <div className="mt-6 flex flex-col gap-2">
                  <Link to="/login">
                    <Button className="w-full bg-[#2D5A4C] hover:bg-[#1E4136] text-white" data-testid="register-goto-login">
                      Aller à la connexion
                    </Button>
                  </Link>
                  <button
                    type="button"
                    onClick={() => setSubmitted(false)}
                    className="text-xs text-[#5E6D66] hover:text-[#2D5A4C] underline underline-offset-4"
                    data-testid="register-back-form"
                  >
                    Modifier mon adresse email
                  </button>
                </div>
              </div>
            ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 bg-[#D47A62]/10 border border-[#D47A62]/20 rounded-lg text-[#D47A62] text-sm" data-testid="register-error">
                  {error}
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="prenom" className="text-[#1A2421]">Prénom</Label>
                  <Input
                    id="prenom"
                    type="text"
                    value={prenom}
                    onChange={(e) => setPrenom(e.target.value)}
                    placeholder="Jean"
                    required
                    className="border-[#E8E6E1] focus:border-[#2D5A4C] focus:ring-[#2D5A4C]/20"
                    data-testid="register-prenom"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nom" className="text-[#1A2421]">Nom</Label>
                  <Input
                    id="nom"
                    type="text"
                    value={nom}
                    onChange={(e) => setNom(e.target.value)}
                    placeholder="Dupont"
                    required
                    className="border-[#E8E6E1] focus:border-[#2D5A4C] focus:ring-[#2D5A4C]/20"
                    data-testid="register-nom"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#1A2421]">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="vous@exemple.com"
                  required
                  className="border-[#E8E6E1] focus:border-[#2D5A4C] focus:ring-[#2D5A4C]/20"
                  data-testid="register-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="raisonSociale" className="text-[#1A2421]">Raison sociale <span className="text-[10px] text-[#5E6D66] font-normal">(optionnel — si personne morale)</span></Label>
                <Input
                  id="raisonSociale"
                  type="text"
                  value={raisonSociale}
                  onChange={(e) => setRaisonSociale(e.target.value)}
                  placeholder="Ex : Coopérative des Îles SARL"
                  className="border-[#E8E6E1] focus:border-[#2D5A4C] focus:ring-[#2D5A4C]/20"
                  data-testid="register-raison-sociale"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="siret" className="text-[#1A2421]">SIRET <span className="text-[10px] text-[#5E6D66] font-normal">(optionnel — 14 chiffres)</span></Label>
                <Input
                  id="siret"
                  type="text"
                  inputMode="numeric"
                  value={siret}
                  onChange={(e) => setSiret(e.target.value)}
                  placeholder="123 456 789 00012"
                  className="border-[#E8E6E1] focus:border-[#2D5A4C] focus:ring-[#2D5A4C]/20"
                  data-testid="register-siret"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="territoire" className="text-[#1A2421] flex items-center gap-1.5">
                  <MapPin size={13} weight="bold" className="text-[#2D5A4C]" /> Territoire <span className="text-[10px] text-[#5E6D66] font-normal">(optionnel)</span>
                </Label>
                <Select value={territoire} onValueChange={setTerritoire}>
                  <SelectTrigger className="border-[#E8E6E1]" data-testid="register-territoire">
                    <SelectValue placeholder="Sélectionner votre territoire" />
                  </SelectTrigger>
                  <SelectContent>
                    {TERRITOIRES.map((t) => (
                      <SelectItem key={t.value} value={t.value} data-testid={`register-territoire-${t.value}`}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#1A2421]">Mot de passe</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="border-[#E8E6E1] focus:border-[#2D5A4C] focus:ring-[#2D5A4C]/20 pr-10"
                    data-testid="register-password"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5E6D66] hover:text-[#1A2421] transition-colors" tabIndex={-1} data-testid="toggle-register-password">
                    {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-[#1A2421]">Confirmer le mot de passe</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="border-[#E8E6E1] focus:border-[#2D5A4C] focus:ring-[#2D5A4C]/20 pr-10"
                    data-testid="register-confirm-password"
                  />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5E6D66] hover:text-[#1A2421] transition-colors" tabIndex={-1} data-testid="toggle-register-confirm-password">
                    {showConfirmPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#2D5A4C] hover:bg-[#1E4136] text-white"
                disabled={loading}
                data-testid="register-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Création...
                  </>
                ) : (
                  'Créer mon compte'
                )}
              </Button>
            </form>
            )}

            {!submitted && (
            <div className="mt-6 text-center">
              <p className="text-sm text-[#5E6D66]">
                Déjà un compte ?{' '}
                <Link to="/login" className="text-[#2D5A4C] hover:text-[#1E4136] font-medium" data-testid="login-link">
                  Se connecter
                </Link>
              </p>
            </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
